/**
 * ProfileCheckRoute - Wrapper that checks if user has completed profile setup
 *
 * Redirects to /profile-setup if user hasn't set a nickname yet
 */

import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { hasCompletedProfile } from '../../lib/queries/profileQueries';

interface ProfileCheckRouteProps {
  children: React.ReactNode;
}

export function ProfileCheckRoute({ children }: ProfileCheckRouteProps) {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);

  useEffect(() => {
    checkProfile();
  }, []);

  const checkProfile = async () => {
    try {
      const completed = await hasCompletedProfile();
      setHasProfile(completed);
    } catch (error) {
      console.error('Error checking profile:', error);
      // On error, assume no profile to be safe
      setHasProfile(false);
    } finally {
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If user doesn't have a profile and is not on profile-setup page, redirect
  if (!hasProfile && location.pathname !== '/profile-setup') {
    return <Navigate to="/profile-setup" replace />;
  }

  return <>{children}</>;
}
